import React, { useState, useEffect } from "react";
import styles from "./ReleaseNotes.module.scss";
import { useTheme } from "hooks/useSingleton";
import { Typography } from "@factorearth/component-library";
import { GraphQLClient, gql } from "graphql-request";

const releaseNotesDocsToken = import.meta.env.VITE_RELEASE_NOTES_AUTH_TOKEN;
const endpoint = import.meta.env.VITE_RELEASE_NOTES_ENDPOINT;

const graphQLClient = new GraphQLClient(endpoint, {
	headers: {
		Authorization: releaseNotesDocsToken,
	},
});

const query = gql`
	{
		pages {
			single(id: 9) {
				path
				title
				scriptJs
				createdAt
				updatedAt
			}
		}
	}
`;

interface ReleaseNotesProps {
	version: string;
}

type Note = {
	title?: string;
	description: string;
};

type Notes = Note[];

type ReleaseNotes = {
	versionNum: string;
	releaseDate: string;
	"New Features": Notes;
	Improvements: Notes;
	"Bug Fixes": Notes;
	Notes: Notes;
};

function ReleaseNotes(props: ReleaseNotesProps) {
	const { version } = props;
	const color = useTheme();
	const [parsedPageContent, setParsedPageContent] = useState<null | ReleaseNotes>(null);
	useEffect(() => {
		const fetchPage = async () => {
			try {
				const result = await graphQLClient.request(query);
				const data = result as { pages: { single: { scriptJs: string } } };
				// const data = (await graphQLClient.request(query)) as { pages: { single: { scriptJs: string } } };
				const wikiJsScript = data.pages.single.scriptJs;
				const releaseData = wikiJsScript.split("const latestReleaseJSON = ")[1].split("const latestRelease = ")[0].trim().replace(/`/g, "");
				const releaseJson = JSON.parse(releaseData);
				setParsedPageContent(releaseJson[0]);
			} catch (error) {
				console.error("Error fetching Wiki.js page:", error);
			}
		};

		fetchPage();
	}, []);

	const buildDescription = (notes: Notes, section: string) => {
		if (!notes || notes.length === 0) {
			return (
				<Typography variant="Small" textColor={color.text.secondary}>
					No content available for {section}
				</Typography>
			);
		}
		return (
			<ul style={{ paddingLeft: 24 }}>
				{notes.map((note, index) => (
					<li key={index} style={{ listStyleType: "disc", marginBottom: 12, color: color.text.primary }}>
						<div>
							{note.title && (
								<Typography variant={"Heading 5"} textColor={color.text.primary}>
									{note.title}
								</Typography>
							)}
							<Typography variant={"Paragraphy Body"} textColor={color.text.primary}>
								{note.description}
							</Typography>
						</div>
					</li>
				))}
			</ul>
		);
	};

	return (
		<div className={styles.releaseNotesPage}>
			<div className={styles.releaseNotesWrapper}>
				{parsedPageContent ? (
					<div
						className={styles.releaseNotesContent}
						style={{
							background: color.background.primary,
							border: `1px solid ${color.background.secondary}`,
						}}>
						<Typography variant={"Heading 5"} textColor={color.text.primary}>
							You are on {version} of Record
						</Typography>
						<div>
							<Typography variant={"Heading 3"} textColor={color.text.primary}>
								FactorEarth Record {parsedPageContent.versionNum} - Release Notes
							</Typography>
							<Typography variant={"Paragraphy Body"} textColor={color.text.primary}>
								Release Date: {parsedPageContent.releaseDate}
							</Typography>
						</div>
						<Typography variant={"Heading 4"} textColor={color.text.primary}>
							🚀 New Features
						</Typography>
						{buildDescription(parsedPageContent["New Features"], "New Features")}
						<Typography variant={"Heading 4"} textColor={color.text.primary}>
							🛠️ Improvements
						</Typography>
						{buildDescription(parsedPageContent["Improvements"], "Improvements")}
						<Typography variant={"Heading 4"} textColor={color.text.primary}>
							🐞 Bug Fixes
						</Typography>
						{buildDescription(parsedPageContent["Bug Fixes"], "Bug Fixes")}
						<Typography variant={"Heading 4"} textColor={color.text.primary}>
							📢 Notes
						</Typography>
						{buildDescription(parsedPageContent["Notes"], "Notes")}
					</div>
				) : (
					<p>loading...</p>
				)}
				<div
					className={styles.contactContainer}
					style={{
						background: color.background.tertiary,
					}}>
					<Typography
						textColor={color.text.primary}
						content={"Please ask further questions, or reach out for additional support by emailing"}
						variant={"Label"}
					/>
					<Typography textColor={color.text.primary} content={"devs@factorearth.com"} variant={"Heading 4"} />
				</div>
			</div>
		</div>
	);
}

export default ReleaseNotes;
